import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Spin } from "antd";
import {
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import MergeVariablesSidebar from "../../../../core/common/email-templates/MergeVariablesSidebar";
import SampleTemplatesPicker from "../../../../core/common/email-templates/SampleTemplatesPicker";
import TemplateVersionsDrawer from "../../../../core/common/email-templates/TemplateVersionsDrawer";
import TemplateCanvas from "../../../../core/common/email-templates/components/TemplateCanvas";
import TemplateFormBar from "../../../../core/common/email-templates/components/TemplateFormBar";
import {
  buildEmailHtml,
  DEFAULT_HEADER_SUBTITLE,
  DEFAULT_HEADER_TITLE,
  DEFAULT_ZONES,
  parseHeaderFromHtml,
  parseThemeFromHtml,
  parseZonesFromHtml,
} from "../../../../core/common/email-templates/helpers/templateHtml.helper";
import {
  DEFAULT_THEME,
  getThemeById,
  loadCompanyTheme,
  themeFromPrimary,
  type EmailTheme,
} from "../../../../core/common/email-templates/data/colorThemes.data";

import {
  createEmailTemplate,
  fetchCategories,
  fetchEmailTemplates,
  updateEmailTemplate,
} from "../../../../core/data/redux/email-templates/thunks";
import {
  selectEmailTemplateCategories,
  selectEmailTemplates,
  selectEmailTemplatesSaving,
} from "../../../../core/data/redux/email-templates/selectors";
import * as emailTemplateApi from "../../../../core/data/redux/services/emailTemplateApi";
import { all_routes } from "../../../router/all_routes";
import { notify } from "../../../../services/toast.config";

import { SAMPLE_TEMPLATES } from "../../../../core/common/email-templates/data/sampleEmailTemplates.data";

import type {
  SampleTemplate,
  TemplateZones,
} from "../../../../core/common/email-templates/data/sampleEmailTemplates.data";
import type { EmailTemplate } from "../../../../core/data/redux/email-templates/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

import "../../../../core/common/email-templates/styles/EmailTemplateModal.css";

interface EditorFormState {
  template_name: string;
  template_code: string;
  subject: string;
  body_html: string;
  category?: string | null;
  available_variables?: any;
}

const EMPTY_FORM: EditorFormState = {
  template_name: "",
  template_code: "",
  subject: "",
  body_html: "",
  category: null,
  available_variables: [],
};

const VALID_CODE_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

const resolveInitialTheme = (): EmailTheme => {
  const saved = loadCompanyTheme();
  const preset = getThemeById(saved);
  return preset ? { ...preset } : { ...DEFAULT_THEME };
};

const templateToForm = (tpl: EmailTemplate): EditorFormState => ({
  template_name: tpl.template_name || "",
  template_code: tpl.template_code || "",
  subject: tpl.subject || "",
  body_html: tpl.body_html || "",
  category: tpl.category || null,
  available_variables: tpl.available_variables || [],
});

const EmailTemplateEditorPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlId } = useParams<{ id?: string }>();

  const templates = useSelector(selectEmailTemplates);
  const categories = useSelector(selectEmailTemplateCategories);
  const saving = useSelector(selectEmailTemplatesSaving);
  const activeCompany = useSelector(
    (state: any) =>
      state.emailTemplates?.activeCompany ||
      state.recruitment?.activeCompany ||
      null,
  );

  const isEdit = Boolean(urlId);
  const duplicateFrom: EmailTemplate | undefined = location.state?.duplicateFrom;

  const editingTemplate = useMemo<EmailTemplate | null>(() => {
    if (!isEdit) return null;
    return templates.find((t) => String(t.id) === String(urlId)) || null;
  }, [isEdit, templates, urlId]);

  const [form, setForm] = useState<EditorFormState>(EMPTY_FORM);
  const [zones, setZones] = useState<TemplateZones>({ ...DEFAULT_ZONES });
  const [theme, setTheme] = useState<EmailTheme>(resolveInitialTheme());
  const [headerTitle, setHeaderTitle] = useState(DEFAULT_HEADER_TITLE);
  const [headerSubtitle, setHeaderSubtitle] = useState(DEFAULT_HEADER_SUBTITLE);
  const [headerEyebrow, setHeaderEyebrow] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const hydratedRef = useRef<string | null>(null);

  const resetCodeStatus = () => {
    setCodeAvailable(null);
    setCodeMessage(null);
  };

  useEffect(() => {
    if (activeCompany?.company_id && categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, activeCompany?.company_id, categories.length]);

  useEffect(() => {
    if (activeCompany?.company_id && templates.length === 0) {
      dispatch(fetchEmailTemplates(undefined));
    }
  }, [dispatch, activeCompany?.company_id, templates.length]);

  const sourceKey = useMemo(() => {
    if (isEdit) return editingTemplate ? `edit:${editingTemplate.id}` : null;
    if (duplicateFrom) return `dup:${duplicateFrom.id}`;
    return "blank";
  }, [isEdit, editingTemplate, duplicateFrom]);

  useEffect(() => {
    if (!sourceKey) return;
    if (hydratedRef.current === sourceKey) return;

    if (isEdit) {
      if (!editingTemplate) return;
      const next = templateToForm(editingTemplate);
      setForm(next);
      setZones(parseZonesFromHtml(next.body_html));
      if (next.body_html) {
        const { title, subtitle, eyebrow } = parseHeaderFromHtml(next.body_html);
        setTheme(parseThemeFromHtml(next.body_html));
        setHeaderTitle(title);
        setHeaderSubtitle(subtitle);
        setHeaderEyebrow(eyebrow);
      }
      hydratedRef.current = sourceKey;
      return;
    }

    if (duplicateFrom) {
      const next: EditorFormState = {
        template_name: `${duplicateFrom.template_name} Copy`,
        template_code: `${duplicateFrom.template_code}_copy`,
        subject: duplicateFrom.subject || "",
        body_html: duplicateFrom.body_html || "",
        category: duplicateFrom.category || null,
        available_variables: duplicateFrom.available_variables || [],
      };
      setForm(next);
      setZones(parseZonesFromHtml(next.body_html));
      if (next.body_html) {
        const { title, subtitle, eyebrow } = parseHeaderFromHtml(next.body_html);
        setTheme(parseThemeFromHtml(next.body_html));
        setHeaderTitle(title);
        setHeaderSubtitle(subtitle);
        setHeaderEyebrow(eyebrow);
      }
      hydratedRef.current = sourceKey;
      return;
    }

    const blank = SAMPLE_TEMPLATES.find((t) => t.id === "blank_page");
    if (blank) {
      setZones({ ...blank.zones });
      setHeaderTitle(blank.headerTitle);
      setHeaderSubtitle(blank.headerSubtitle);
      setHeaderEyebrow("");
    }
    hydratedRef.current = sourceKey;
  }, [sourceKey, isEdit, editingTemplate, duplicateFrom]);

  useEffect(() => {
    if (!isEdit && !duplicateFrom && form.template_name && !form.body_html) {
      setHeaderTitle(form.template_name);
    }
  }, [form.template_name, form.body_html, isEdit, duplicateFrom]);

  const handleZoneChange = useCallback(
    (zone: keyof TemplateZones, html: string) => {
      setZones((prev) => ({ ...prev, [zone]: html }));
    },
    [],
  );

  const handleSampleSelect = useCallback(
    (tpl: SampleTemplate) => {
      setForm((prev) => ({
        ...prev,
        template_name: tpl.name,
        template_code: tpl.template_code || prev.template_code || "",
        subject: tpl.subject,
      }));
      setZones({ ...tpl.zones });
      setTheme(themeFromPrimary(tpl.accent));
      setHeaderTitle(tpl.headerTitle);
      setHeaderSubtitle(tpl.headerSubtitle);
      setHeaderEyebrow(tpl.headerEyebrow || "");
      notify.success(`"${tpl.name}" loaded — customise and save.`);
    },
    [],
  );

  const handleCheckCode = useCallback(async () => {
    const trimmed = (form.template_code || "").trim();
    if (!trimmed) {
      setCodeAvailable(false);
      setCodeMessage("Template code is required");
      return;
    }
    if (!VALID_CODE_PATTERN.test(trimmed)) {
      setCodeAvailable(false);
      setCodeMessage(
        "Use letters, numbers and underscores. Must start with a letter.",
      );
      return;
    }
    if (!activeCompany?.company_id || !activeCompany?.company_type) {
      notify.warn("Select a company first to check availability.");
      return;
    }
    try {
      setCodeChecking(true);
      const result = await emailTemplateApi.checkTemplateCodeAvailability(
        activeCompany.company_id,
        activeCompany.company_type,
        trimmed,
        editingTemplate?.id,
      );
      if (result?.available) {
        setCodeAvailable(true);
        setCodeMessage(`'${trimmed}' is available`);
      } else {
        setCodeAvailable(false);
        setCodeMessage(
          result?.conflicting_template_name
            ? `Already used by '${result.conflicting_template_name}'`
            : `'${trimmed}' is already taken`,
        );
      }
    } catch {
      setCodeAvailable(null);
      setCodeMessage("Could not verify availability");
    } finally {
      setCodeChecking(false);
    }
  }, [
    form.template_code,
    activeCompany?.company_id,
    activeCompany?.company_type,
    editingTemplate?.id,
  ]);

  const handleCancel = () => {
    navigate(all_routes.emailTemplates);
  };

  const handleSave = async () => {
    if (!activeCompany?.company_id) {
      notify.warn("Please select a company first.");
      return;
    }
    if (!form.template_name || !form.template_code || !form.subject) {
      notify.warn("Please fill Template Name, Template Code, and Subject.");
      return;
    }
    if (codeAvailable === false) {
      notify.warn(codeMessage || "Template code is not available.");
      return;
    }
    if (!isEdit && !VALID_CODE_PATTERN.test(form.template_code.trim())) {
      notify.warn(
        "Template code must start with a letter and only contain letters, numbers, and underscores.",
      );
      return;
    }
    if (!zones.body.trim() || zones.body === "<p></p>") {
      notify.warn("Please add content to the Body zone.");
      return;
    }

    const body_html = buildEmailHtml(
      zones,
      theme,
      headerTitle,
      headerSubtitle,
      headerEyebrow,
    )
      .replace(/%7B%7B/g, "{{")
      .replace(/%7D%7D/g, "}}");

    const payload = {
      ...form,
      body_html,
      available_variables: sidebarVariables,
    };

    try {
      if (editingTemplate) {
        await dispatch(
          updateEmailTemplate({
            templateId: editingTemplate.id,
            template: payload,
          }),
        ).unwrap();
        notify.success("Template updated");
      } else {
        await dispatch(createEmailTemplate(payload)).unwrap();
        notify.success("Template created");
      }
      dispatch(fetchEmailTemplates(undefined));
      navigate(all_routes.emailTemplates);
    } catch (error: any) {
      notify.error(
        typeof error === "string" ? error : "Failed to save template",
      );
    }
  };

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.is_active)
        .map((c) => ({
          value: c.value,
          label: c.icon ? `${c.icon}  ${c.label}` : c.label,
        })),
    [categories],
  );

  const SYSTEM_VAR_KEYS = useMemo(
    () =>
      new Set([
        "companyName",
        "productName",
        "logoUrl",
        "year",
        "supportEmail",
        "hrEmail",
        "footerNote",
      ]),
    [],
  );

  const derivedVariableKeys = useMemo(() => {
    const sources = [
      form.subject || "",
      headerTitle || "",
      headerSubtitle || "",
      headerEyebrow || "",
      zones.greeting || "",
      zones.body || "",
      zones.closing || "",
    ].join("\n");
    const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
    const seen = new Set<string>();
    let m;
    while ((m = re.exec(sources)) !== null) {
      if (!SYSTEM_VAR_KEYS.has(m[1])) seen.add(m[1]);
    }
    return Array.from(seen).sort();
  }, [
    form.subject,
    headerTitle,
    headerSubtitle,
    headerEyebrow,
    zones.greeting,
    zones.body,
    zones.closing,
    SYSTEM_VAR_KEYS,
  ]);

  const sidebarVariables = useMemo(() => {
    const declared = Array.isArray(form.available_variables)
      ? form.available_variables
      : [];
    const declaredKeys = new Set(
      declared.map((v: any) => (typeof v === "string" ? v : v?.key)),
    );
    const extras = derivedVariableKeys
      .filter((k) => !declaredKeys.has(k))
      .map((k) => ({ key: k, type: "string" as const }));
    return [...declared, ...extras];
  }, [derivedVariableKeys, form.available_variables]);

  const editingButMissing = isEdit && !editingTemplate;
  const pageTitle = editingTemplate ? "Edit Email Template" : "Create Email Template";
  const breadcrumb = editingTemplate ? "Edit" : "Create";

  const headerDescription = editingTemplate
    ? "Update the layout, copy, and theme. Saved changes create a new version of this template."
    : "Design a reusable email — pick a sample to start, customise the zones inline, and save when you're happy.";

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Email Templates</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.emailTemplates}>Email Templates</Link>
                  </li>
                  <li className="breadcrumb-item active">{breadcrumb}</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <button
                  className="btn btn-outline-secondary d-inline-flex align-items-center"
                  onClick={handleCancel}
                >
                  <i className="ti ti-arrow-left me-2" />
                  Back to Templates
                </button>
              </div>

              <div className="d-flex align-items-center gap-2 mb-2">
                {editingTemplate ? (
                  <Button
                    icon={<HistoryOutlined />}
                    onClick={() => setVersionsOpen(true)}
                  >
                    Version history
                  </Button>
                ) : null}
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => setPickerOpen(true)}
                >
                  Sample Templates
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                >
                  {editingTemplate ? "Save Changes" : "Create Template"}
                </Button>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body bg-white rounded-3">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                    {editingTemplate ? (
                      <EditOutlined style={{ color: "#3730a3", fontSize: 18 }} />
                    ) : (
                      <PlusOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                    )}
                    <h4 className="mb-0">{pageTitle}</h4>
                    {editingTemplate?.version ? (
                      <span
                        style={{
                          fontSize: 10,
                          background: "#eef2ff",
                          color: "#3730a3",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontWeight: 600,
                        }}
                      >
                        v{editingTemplate.version}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    {headerDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {editingButMissing ? (
            <div className="card">
              <div className="card-body text-center py-5 bg-white rounded-3">
                <Spin />
                <p className="text-muted mt-3 mb-0">Loading template…</p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body bg-white rounded-3">
                <Form layout="vertical" size="middle">
                  <TemplateFormBar
                    templateName={form.template_name}
                    templateCode={form.template_code}
                    category={form.category}
                    subject={form.subject}
                    onTemplateNameChange={(v) =>
                      setForm((p) => ({ ...p, template_name: v }))
                    }
                    onTemplateCodeChange={(v) => {
                      setForm((p) => ({ ...p, template_code: v }));
                      resetCodeStatus();
                    }}
                    onCategoryChange={(v) =>
                      setForm((p) => ({ ...p, category: v }))
                    }
                    onSubjectChange={(v) =>
                      setForm((p) => ({ ...p, subject: v }))
                    }
                    categoryOptions={categoryOptions}
                    codeChecking={codeChecking}
                    codeAvailable={codeAvailable}
                    codeMessage={codeMessage}
                    onCheckCode={handleCheckCode}
                    isEditing={Boolean(editingTemplate)}
                  />
                </Form>

                <div className="email-modal-body" style={{ minHeight: 540 }}>
                  <TemplateCanvas
                    zones={zones}
                    theme={theme}
                    headerTitle={headerTitle}
                    headerSubtitle={headerSubtitle}
                    headerEyebrow={headerEyebrow}
                    onZoneChange={handleZoneChange}
                    onThemeChange={setTheme}
                    onHeaderTitleChange={setHeaderTitle}
                    onHeaderSubtitleChange={setHeaderSubtitle}
                    onHeaderEyebrowChange={setHeaderEyebrow}
                  />

                  <MergeVariablesSidebar
                    availableVariables={sidebarVariables}
                    onInsert={() => { }}
                    onChangeVariables={(next) =>
                      setForm((p) => ({ ...p, available_variables: next }))
                    }
                    editable
                    lockedBinding={null}
                  />
                </div>

                <div className="d-flex justify-content-end mt-3 gap-2">
                  <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                  >
                    {editingTemplate ? "Save Changes" : "Create Template"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="footer border-top bg-white p-3 text-center">
          <p className="mb-0" style={{ fontSize: 12, color: "#64748b" }}>
            ZYVO · A product by Lakhera Global Services
          </p>
          <p className="mb-0" style={{ fontSize: 12, color: "#64748b" }}>
            © {new Date().getFullYear()} Lakhera Global Services. All rights reserved.
          </p>
        </div>
      </div>

      <SampleTemplatesPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(tpl) => {
          handleSampleSelect(tpl);
          setPickerOpen(false);
        }}
      />

      <TemplateVersionsDrawer
        open={versionsOpen}
        templateId={editingTemplate?.id || null}
        companyId={activeCompany?.company_id}
        companyType={activeCompany?.company_type}
        currentTemplate={editingTemplate}
        onClose={() => setVersionsOpen(false)}
        onRestored={() => {
          setVersionsOpen(false);
          dispatch(fetchEmailTemplates(undefined));
          hydratedRef.current = null;
        }}
      />
    </>
  );
};

export default EmailTemplateEditorPage;
